import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Outlet, useLocation } from 'react-router-dom';
import AppBar from './AppBar';
import Sidebar from './Sidebar';
import AIAssistantButton from '../components/ai/AIAssistantButton';
import CalibrationDueAlert from '../components/inventory/CalibrationDueAlert';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '@mui/material/styles';

const getPageTitle = (pathname) => {
    // Simple mapping, you can expand this as needed
    if (pathname === '/') return 'Activity Dashboard';
    if (pathname.startsWith('/service-orders')) return 'Service Orders';
    if (pathname.startsWith('/ar-dashboard')) return 'Invoice & Billing';
    if (pathname.startsWith('/invoicing')) return 'Invoice & Billing';
    if (pathname.startsWith('/inventory')) return 'Inventory Management';
    if (pathname.startsWith('/users')) return 'Users';
    if (pathname.startsWith('/documents')) return 'Document Management';
    if (pathname.startsWith('/reporting')) return 'Reporting';
    if (pathname.startsWith('/customer/reports')) return 'Reporting';
    if (pathname.startsWith('/flash-report')) return 'Reporting';
    if (pathname.startsWith('/detailed-report')) return 'Reporting';
    if (pathname.startsWith('/reports/')) return 'Reporting';
    if (pathname.startsWith('/training')) return 'Training';
    if (pathname.startsWith('/master-data')) return 'Master Data Management';
    if (pathname.startsWith('/settings')) return 'Settings';
    if (pathname.startsWith('/reflection-dashboard')) return 'Reflection Dashboard';
    if (pathname.startsWith('/workforce-management')) return 'Workforce Management';
    if (pathname.startsWith('/attendance')) return 'Workforce Management';
    if (pathname.startsWith('/leave')) return 'Workforce Management';
    if (pathname.startsWith('/expenses')) return 'Workforce Management';
    return '';
};

const MainLayout = () => {
    const theme = useTheme();
    const location = useLocation();
    const pageTitle = getPageTitle(location.pathname);
    const { user } = useAuth();
    // Gated on inventory:change, not just :view — the alert exists to prompt action
    // (updating next_calibration_date), so view-only access shouldn't see it.
    // Mirrors the backend's CalibrationAlertPermission gate.
    const canSeeCalibrationAlert = user?.role?.permissions?.inventory?.view === true
        && user?.role?.permissions?.inventory?.change === true;

    // Sidebar visibility state - persist in localStorage
    const [sidebarOpen, setSidebarOpen] = useState(() => {
        const saved = localStorage.getItem('sidebarOpen');
        return saved !== null ? JSON.parse(saved) : true;
    });

    useEffect(() => {
        localStorage.setItem('sidebarOpen', JSON.stringify(sidebarOpen));
    }, [sidebarOpen]);

    const toggleSidebar = useCallback(() => {
        setSidebarOpen(prev => !prev);
    }, []);

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <Sidebar open={sidebarOpen} />
            <AppBar pageTitle={pageTitle} sidebarOpen={sidebarOpen} onToggleSidebar={toggleSidebar} />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    bgcolor: 'background.default',
                    mt: '72px', // header height (adjust if your header is taller/shorter)
                    ml: sidebarOpen ? `${theme.sidebar.width}px` : 0, // Add margin for fixed sidebar
                    width: sidebarOpen ? `calc(100% - ${theme.sidebar.width}px)` : '100%',
                    transition: theme.transitions.create(['margin', 'width'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    p: 1,
                }}
            >
                <Suspense fallback={
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                        <CircularProgress />
                    </Box>
                }>
                    {canSeeCalibrationAlert && <CalibrationDueAlert />}
                    <Outlet />
                </Suspense>
            </Box>
            <AIAssistantButton />
        </Box>
    );
};

export default MainLayout; 